import React from 'react';
import {
  CHORD_DEGREE_BUTTONS,
  MELODY_KEYS,
  SUBDIVISION_OPTIONS,
  type BuildTool,
  type ChordDegreeId,
  type MelodyKeyId,
  type Subdivision,
} from '../../lib/melody/melodiaKeys';
import { MAX_MEASURES, type MelodiaSessionConfig, type MelodyPitchId } from '../../lib/melody/melodiaBuilderConfig';
import { MelodiaGridZone } from './MelodiaGridZone';

const TOOLS: { id: BuildTool; icon: string; label: string }[] = [
  { id: 'pencil', icon: '✏️', label: 'Lápiz — colocar o quitar notas' },
  { id: 'lasso', icon: '◯', label: 'Lazo — seleccionar varias notas' },
];

const SUB_ORDER: Subdivision[] = [1, 2, 4];

function subdivIcons(n: Subdivision): string {
  return '♪'.repeat(n);
}

interface MelodiaConstructPanelProps {
  config: MelodiaSessionConfig;
  activeStep: number;
  isPlaying: boolean;
  tool: BuildTool;
  activeChord: ChordDegreeId | null;
  selection: Set<string>;
  selectedPitch: MelodyPitchId;
  canUndo: boolean;
  onTool: (tool: BuildTool) => void;
  onUndo: () => void;
  onClearSelection: () => void;
  onKeyChange: (keyId: MelodyKeyId) => void;
  onSubdivisionChange: (sub: Subdivision) => void;
  onChordSelect: (degree: ChordDegreeId | null) => void;
  onSelectionChange: (keys: Set<string>) => void;
  onSelectPitch: (pitchId: MelodyPitchId) => void;
  onCellClick: (measureIndex: number, visualSlot: number, pitchId: MelodyPitchId) => void;
  onMeasureCountChange: (count: number) => void;
}

export const MelodiaConstructPanel: React.FC<MelodiaConstructPanelProps> = ({
  config,
  activeStep,
  isPlaying,
  tool,
  activeChord,
  selection,
  selectedPitch,
  canUndo,
  onTool,
  onUndo,
  onClearSelection,
  onKeyChange,
  onSubdivisionChange,
  onChordSelect,
  onSelectionChange,
  onSelectPitch,
  onCellClick,
  onMeasureCountChange,
}) => {
  const subdivIdx = SUB_ORDER.indexOf(config.subdivision);
  const subdivCurrent = SUBDIVISION_OPTIONS.find((o) => o.value === config.subdivision);

  const stepSub = (delta: -1 | 1) => {
    const next = SUB_ORDER[Math.min(SUB_ORDER.length - 1, Math.max(0, subdivIdx + delta))];
    if (next !== config.subdivision) onSubdivisionChange(next);
  };

  return (
    <div className="melodia-construct-panel flex flex-col flex-1 min-w-0 min-h-0">
      <div className="melodia-panel-toolbar shrink-0">
        <div className="melodia-panel-group melodia-panel-tools" aria-label="Herramientas">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={isPlaying}
              onClick={() => onTool(t.id)}
              className={`melodia-tool-btn ${tool === t.id ? 'melodia-tool-btn--active' : ''}`}
              title={t.label}
              aria-label={t.label}
              aria-pressed={tool === t.id}
            >
              {t.icon}
            </button>
          ))}
          <button
            type="button"
            disabled={isPlaying || !canUndo}
            onClick={onUndo}
            className="melodia-tool-btn"
            title="Deshacer"
            aria-label="Deshacer"
          >
            ↩
          </button>
        </div>

        <div className="melodia-panel-divider" aria-hidden />

        <div className="melodia-panel-group melodia-panel-subdiv">
          <span className="melodia-panel-label">Grilla</span>
          <div className="melodia-subdiv-stepper melodia-subdiv-stepper--inline">
            <button
              type="button"
              className="melodia-subdiv-arrow"
              disabled={isPlaying || subdivIdx <= 0}
              onClick={() => stepSub(-1)}
              aria-label="Grilla más gruesa"
            >
              ◀
            </button>
            <div className="melodia-subdiv-display">
              <span className="melodia-subdiv-icons" aria-hidden>
                {subdivIcons(config.subdivision)}
              </span>
              <span className="melodia-subdiv-hint">{subdivCurrent?.label ?? ''}</span>
            </div>
            <button
              type="button"
              className="melodia-subdiv-arrow"
              disabled={isPlaying || subdivIdx >= SUB_ORDER.length - 1}
              onClick={() => stepSub(1)}
              aria-label="Grilla más fina"
            >
              ▶
            </button>
          </div>
        </div>

        <div className="melodia-panel-divider" aria-hidden />

        <div className="melodia-panel-group melodia-panel-chords">
          <span className="melodia-panel-label">Acordes</span>
          <div className="melodia-chord-btns melodia-chord-btns--inline">
            {CHORD_DEGREE_BUTTONS.map((deg) => (
              <button
                key={deg}
                type="button"
                disabled={isPlaying}
                onClick={() => onChordSelect(activeChord === deg ? null : deg)}
                className={`melodia-chord-btn ${activeChord === deg ? 'melodia-chord-btn--active' : ''}`}
                title={`Resaltar acorde ${deg}`}
              >
                {deg}
              </button>
            ))}
          </div>
        </div>

        <div className="melodia-panel-spacer" />

        {selection.size > 0 && (
          <div className="melodia-selection-badge">
            <span>{selection.size} sel.</span>
            <button type="button" onClick={onClearSelection} className="melodia-selection-clear">
              ✕
            </button>
          </div>
        )}

        <div className="melodia-panel-group melodia-panel-measures">
          <span className="melodia-panel-label">Compases</span>
          <div className="melodia-measure-stepper">
            <button
              type="button"
              onClick={() => onMeasureCountChange(config.measureCount - 1)}
              disabled={config.measureCount <= 1 || isPlaying}
              className="eco-chip-btn"
              aria-label="Quitar compás"
            >
              −
            </button>
            <span className="eco-chip-count">{config.measureCount}</span>
            <button
              type="button"
              onClick={() => onMeasureCountChange(config.measureCount + 1)}
              disabled={config.measureCount >= MAX_MEASURES || isPlaying}
              className="eco-chip-btn"
              aria-label="Añadir compás"
            >
              +
            </button>
          </div>
        </div>
      </div>

      <MelodiaGridZone
        config={config}
        activeStep={activeStep}
        isPlaying={isPlaying}
        tool={tool}
        activeChord={activeChord}
        selection={selection}
        onSelectionChange={onSelectionChange}
        selectedPitch={selectedPitch}
        onSelectPitch={onSelectPitch}
        onCellClick={onCellClick}
      />

      <div className="melodia-panel-footer shrink-0">
        <label className="melodia-panel-label" htmlFor="melodia-key-select">
          Tono
        </label>
        <select
          id="melodia-key-select"
          className="melodia-key-select melodia-key-select--footer"
          value={config.keyId}
          disabled={isPlaying}
          onChange={(e) => onKeyChange(e.target.value as MelodyKeyId)}
        >
          {MELODY_KEYS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default MelodiaConstructPanel;
